import { getErrorMessage } from '@givebacks/utils';

import { BetterApiRefreshResponseError } from './types';

class Refresher<TRefreshResponse extends Record<string, unknown>> {
  isRefreshing: boolean;
  /**
   * The queue of pending promises
   */
  refreshQueue: Set<unknown>;

  /**
   * Determines if the queue is being processed
   */
  isProcessingQueue: boolean;
  private getRefreshToken: () => Promise<TRefreshResponse>;

  constructor({ getRefreshToken }: { getRefreshToken: () => Promise<TRefreshResponse> }) {
    this.isRefreshing = false;
    this.refreshQueue = new Set();
    this.getRefreshToken = getRefreshToken;
    this.isProcessingQueue = false;
  }

  /**
   * Refresh the session
   * If the session is already being refreshed, add the promise to the queue
   * Otherwise, set the refreshing flag to true and get the new session
   * @returns The new session
   */
  async refresh(
    options: {
      onRefreshToken: (response: TRefreshResponse | BetterApiRefreshResponseError) => void;
    } = {
      onRefreshToken: () => {},
    }
  ): Promise<BetterApiRefreshResponseError | TRefreshResponse> {
    if (this.isRefreshing) {
      return new Promise<BetterApiRefreshResponseError | TRefreshResponse>((resolve, reject) => {
        this.refreshQueue.add({ resolve, reject });
      })
        .then((token) => {
          return token;
        })
        .catch((error: Error) => {
          return Promise.reject(error);
        });
    } else {
      this.isRefreshing = true;
      let response: TRefreshResponse | BetterApiRefreshResponseError = { error: 'Unknown error' };
      try {
        const newSession = await this.getRefreshToken();
        response = newSession;
      } catch (e) {
        const errorMessage = getErrorMessage(e);
        response = { error: errorMessage };
      } finally {
        options.onRefreshToken(response);
        this.isRefreshing = false;
      }

      return response;
    }
  }

  /**
   * Process the queue of pending promises
   * @param error - The error to reject the promise with
   * @param token - The token to resolve the promise with
   */
  processQueue(error: boolean | Error, token: TRefreshResponse | null = null) {
    if (this.isProcessingQueue) {
      return;
    }

    this.isProcessingQueue = true;
    this.refreshQueue.forEach((promise: any) => {
      if (error) {
        promise.reject(error);
      } else {
        promise.resolve(token);
      }
      this.refreshQueue.delete(promise);
    });

    this.isProcessingQueue = false;
  }
}

export { Refresher };
